import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SyncIcon from "@mui/icons-material/Sync";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetProgramFeesAndExpenses,
  useGetProgramStatusListId,
} from "../../../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiTypography,
} from "../../../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { ProgramStatusResult } from "../../../../../viewProgram/components/programAndParticipant/tabs/participant/components/programStatus/ProgramStatusResult";
import {
  ProgramDetail as ProgramDetailType,
  ProgramSectionKey,
  ProgramTabFormKey,
} from "../../../../modelsAndSchemas/Models";
import {
  programSiteCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../../../siteCore/SiteCoreKeys";
import { ChangeProgramStatus } from "./components/changeProgramStatus/ChangeProgramStatus";
import { ProgramDetail } from "./components/programDetail/ProgramDetail";
import { ProgramOtherDetail } from "./components/programOtherDetail/ProgramOtherDetail";

import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { ProgramExpenseData } from "../../../../../../api/services/program/models";
import { ButtonWithIcon } from "../../../../../../components/buttonWithIcon/ButtonWithIcon";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { UserDetails } from "../../../../../../helpers/types/common";
import { getValueOrBlank, isNotEmpty } from "../../../../../../helpers/utils";
import { programStatusSelector } from "../../../../../../redux/slices/program/programSelector";
import { isFutureDateValidation } from "../../../../modelsAndSchemas/ValidationSchema";
import { SalesCreditDetails } from "./components/salesCredit/SalesCreditDetails";

interface ProgramTabProps {
  pageId: string;
  programDetail?: ProgramDetailType;
  clientRelationshipOwnerList: UserDetails[];
  isEditProgram: boolean;
  programId: string;
  isFromTransfer?: boolean;
  onRefreshProgramDetail?: (id: string) => void;
  isFromCoachPortal?: boolean;
}

const initialAccordionStatus = {
  programDetail: true,
  all: true,
  programStatus: true,
  programOtherDetail: true,
  salesCredit: false,
};

export const ProgramTab: FC<ProgramTabProps> = ({
  pageId,
  programDetail,
  isEditProgram,
  programId,
  isFromTransfer = false,
  clientRelationshipOwnerList,
  onRefreshProgramDetail,
  isFromCoachPortal = false,
}) => {
  const programStatus = useSelector(programStatusSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const dispatch = useDispatch();
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const {
    fetchComponentTitle,
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    handleAccordionExpansionState,
  } = useAccordion(initialAccordionStatus);

  const {
    formState: { errors },
  } = useFormContext();

  const {
    data: programStatusList,
    isFetching: programStatusListFetching,
    isError: isProgramStatusListError,
    refetchWithParams: refetchProgramStatusList,
  } = useGetProgramStatusListId();

  const { data: expenseList, mutateAsync: getProgramFeesAndExpenses } =
    useGetProgramFeesAndExpenses();

  useEffect(() => {
    if (isEditProgram) {
      refetchProgramStatusList({ programId });
      (programStatus === STATUS_BADGE_TYPE.open ||
        programStatus === STATUS_BADGE_TYPE.active) &&
        getProgramFeesAndExpenses(programId);
    }
  }, [programId, programStatus]);

  useEffect(() => {
    if (isProgramStatusListError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramStatusListError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: programStatusListFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [programStatusListFetching]);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const renderStatusHeader = (labelKey: string) => {
    return <MuiBox className="status-header">{renderHeader(labelKey)}</MuiBox>;
  };

  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  //allow to edit field based on status
  const editableProgramFields = useMemo(
    () => ({
      isDetailFieldsEditable:
        programStatus === STATUS_BADGE_TYPE.open ||
        programStatus === STATUS_BADGE_TYPE.active ||
        programStatus === STATUS_BADGE_TYPE.inActive,

      isOtherFieldsEditable:
        programStatus === STATUS_BADGE_TYPE.open ||
        programStatus === STATUS_BADGE_TYPE.active,
    }),
    [programStatus],
  );

  const sectionFieldsMap: Record<ProgramSectionKey, string[]> = {
    programDetail: [
      ProgramTabFormKey.prefix,
      ProgramTabFormKey.firstName,
      ProgramTabFormKey.lastName,
      ProgramTabFormKey.programName,
      ProgramTabFormKey.solutionOffering,
      ProgramTabFormKey.programClass,
      ProgramTabFormKey.businessUnit,
      ProgramTabFormKey.costCentre,
      ProgramTabFormKey.poNumber,
      ProgramTabFormKey.site,
    ],
    programOtherDetail: [
      ProgramTabFormKey.billingOffice,
      ProgramTabFormKey.salesOffice,
      ProgramTabFormKey.clientRelationShipOwner,
      ProgramTabFormKey.deliveryOffice,
      ProgramTabFormKey.billingContact,
      ProgramTabFormKey.hrContact,
      ProgramTabFormKey.projectManager,
      ProgramTabFormKey.projectCoordinator,
      ProgramTabFormKey.participantManagerId,
      ProgramTabFormKey.additionalInvoiceContactId,
    ],
  };

  const hasErrors = (section: ProgramSectionKey) => {
    const fields = sectionFieldsMap[section];
    return fields.some((field) => errors[field]);
  };

  useEffect(() => {
    (Object.keys(sectionFieldsMap) as ProgramSectionKey[]).forEach(
      (sectionKey: ProgramSectionKey) => {
        if (hasErrors(sectionKey)) {
          handleAccordionExpansionState(sectionKey.toString(), true);
        }
      },
    );
  }, [errors]);

  const isUnbilledExpenses: boolean = useMemo((): boolean => {
    // Check if there are any unbilled expenses && no project tagged to the program
    return (
      !programDetail?.isProjectTagged &&
      isNotEmpty(expenseList) &&
      expenseList.some(
        (expense: ProgramExpenseData) =>
          getValueOrBlank(expense.statusId) === STATUS_BADGE_TYPE.unBilled,
      )
    );
  }, [expenseList, programDetail]);

  const disabledChangeStatus: boolean = useMemo((): boolean => {
    if (
      isNotEmpty(programStatusList) &&
      programStatus === STATUS_BADGE_TYPE.closed
    ) {
      return (
        programStatusList.length > 0 &&
        isFutureDateValidation(
          getValueOrBlank(programStatusList[0]?.fromDate),
          getValueOrBlank(userCountryDateFormat),
        ) &&
        programStatusList[0]?.status !== STATUS_BADGE_TYPE.closed
      );
    }
    return false;
  }, [programStatusList, programStatus]);

  return (
    <>
      <MuiBox className="header-collapse">
        <MuiButtonTertiary
          id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
          onClick={handleExpandCollapseAllChange}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
        </MuiButtonTertiary>
      </MuiBox>
      <PermissionWrapper
        permission={
          isFromCoachPortal
            ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_PROGRAM_DETAILS
            : MODULE_PERMISSIONS.EDIT_PROGRAM_DETAILS
        }
      >
        <AccordionHeader
          title={renderHeader(programSiteCoreKeys.programDetails)}
          onChange={() => handleIndividualAccordionChange("programDetail")}
          isExpanded={accordionStatus.programDetail}
        >
          <ProgramDetail
            pageId={pageId}
            programData={programDetail}
            isFieldDisabled={
              isEditProgram
                ? !editableProgramFields.isDetailFieldsEditable
                : false
            } // if filed are not editable then disable the fields
            isFromCoachPortal={isFromCoachPortal}
            isEditProgram={isEditProgram}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <PermissionWrapper
        permission={
          isFromCoachPortal
            ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_OTHER_DETAILS
            : MODULE_PERMISSIONS.EDIT_OTHER_DETAILS
        }
      >
        <MuiBox className="accordion-header">
          <AccordionHeader
            title={renderHeader(programSiteCoreKeys.officeDetailsKey)}
            onChange={() =>
              handleIndividualAccordionChange("programOtherDetail")
            }
            isExpanded={accordionStatus.programOtherDetail}
          >
            <ProgramOtherDetail
              pageId={pageId}
              programData={programDetail}
              isFieldDisabled={
                isEditProgram
                  ? !editableProgramFields.isOtherFieldsEditable
                  : false
              } // if filed are not editable then disable the fields
              isFromTransfer={isFromTransfer}
              isFromCoachPortal={isFromCoachPortal}
            />
          </AccordionHeader>
        </MuiBox>
      </PermissionWrapper>

      <PermissionWrapper
        permission={
          isFromCoachPortal
            ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_PROGRAM_STATUS
            : MODULE_PERMISSIONS.EDIT_PROGRAM_STATUS
        }
      >
        <MuiBox className="accordion-header">
          <AccordionHeader
            title={renderStatusHeader(programSiteCoreKeys.programStatus)}
            onChange={() => handleIndividualAccordionChange("programStatus")}
            isExpanded={accordionStatus.programStatus}
          >
            <MuiBox className="status-label">
              <MuiTypography variant="h3">
                {fetchLabel(
                  programSiteCoreKeys.programStatus,
                  programSiteCoreKeys.currentStatus,
                )}
              </MuiTypography>
              <MuiBox className="active-change-status">
                <StatusBadge
                  status={programStatus as StatusBadgeType}
                  label={fetchValueByKeyOfDropdown(
                    programSiteCoreKeys.generalDetails,
                    programSiteCoreKeys.statusList,
                    programStatus as StatusBadgeType,
                  )}
                />
                {isEditProgram && (
                  <ButtonWithIcon
                    buttonText={fetchLabel(
                      programSiteCoreKeys.programStatusKey,
                      programSiteCoreKeys.changeStatusPopupHeader,
                    )}
                    ActionIcon={SyncIcon}
                    data-testid={`${pageId}-change-status`}
                    onActionClick={() => setShowChangeStatusModal(true)}
                    disabled={disabledChangeStatus}
                  />
                )}
              </MuiBox>
              {isEditProgram && (
                <MuiBox className="program-status-list">
                  <ProgramStatusResult
                    rows={programStatusList ?? []}
                    pageId={pageId}
                    isEditProgram={true}
                    refetchProgramStatusList={() =>
                      refetchProgramStatusList({ programId })
                    }
                    showEditForReopen={disabledChangeStatus}
                    onRefreshProgramDetail={onRefreshProgramDetail}
                    deliveryCountry={programDetail?.deliveryCountry}
                  />
                </MuiBox>
              )}
            </MuiBox>
          </AccordionHeader>
        </MuiBox>
      </PermissionWrapper>

      {!isFromCoachPortal && (
        <PermissionWrapper permission={MODULE_PERMISSIONS.EDIT_SALES_CREDIT}>
          <MuiBox className="accordion-header">
            <AccordionHeader
              title={renderHeader(salesCreditSiteCoreKeys.salesCredit)}
              onChange={() => handleIndividualAccordionChange("salesCredit")}
              isExpanded={accordionStatus.salesCredit}
            >
              <SalesCreditDetails
                clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
                pageId={pageId}
                isEditProgram={isEditProgram}
                isInViewMode={false}
                resultsFormKey={ProgramTabFormKey.salesCreditDetails}
              />
            </AccordionHeader>
          </MuiBox>
        </PermissionWrapper>
      )}

      {showChangeStatusModal && (
        <ChangeProgramStatus
          pageId={pageId}
          open={showChangeStatusModal}
          setShowChangeStatusModal={setShowChangeStatusModal}
          programId={programId}
          isEditProgram={isEditProgram}
          refetchProgramStatusList={() =>
            refetchProgramStatusList({ programId })
          }
          onRefreshProgramDetail={onRefreshProgramDetail}
          selectedDeliveryCountryId={programDetail?.deliveryCountry}
          programStartDate={programDetail?.programStartDate}
          closingReasonTypeKey={programDetail?.closingReasonTypeKey}
          isUnbilledExpenses={isUnbilledExpenses}
        />
      )}
    </>
  );
};
